<template>
  <button :style="buttonStyles" @click="handleButtonClick">
    <div :style="logoContainerStyles">
      <img :style="logoImageStyles" :src="logoSrc" alt="Logo" />
    </div>
    <span>{{ buttonText }}</span>
  </button>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  theme: {
    type: String,
    default: "primary",
    validator: (value) => ["primary", "dark", "light"].includes(value),
  },
  buttonText: {
    type: String,
    default: "Continue with Frequency",
  },
  width: {
    type: String,
    default: "300px",
  },
});

const emit = defineEmits(["click"]);

const colors = {
  bg: {
    primary: "#54B2AB",
    light: "#ffffff",
    dark: "#000000",
  },
  fg: {
    primary: "#000000",
    light: "#000000",
    dark: "#ffffff",
  },
  border: {
    primary: "#54B2AB",
    light: "#000000",
    dark: "#000000",
  },
  logo: {
    primary:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEIAAABCCAQAAABJXchjAAACrklEQVR42s3ZT2scZQDH8Q+bwxptvXhQ2kyikaKxWASxnrV/PIVSeog5C74Eb9KQQ7v1IF3BxgT0EKQ1N6EHD76ETRM8aYhb281GBLOhF7G7CXk8B7NmZ+bZGb+/+/KBgZ2ZZ2QoMWveinVNu7q6djWt+c68DyWG2ojLlmwKx2zToksqojfulraQYm0146I1qe6pkGE9y16Xu+fU7Ak5tqfueTm6ZluIsLarMlVVFyJu2aiUTVgXIm9NIkVTWoL4+905A/aujjCkdZw3QGd1hCHuiXOOaaLPX9K+3QzbF45YS0L/qtb6+DdkaUM4cj95Rt8WhEIQwRf6dE0oDBFccUQntQtFbDnhX90WCkUEn3G41+wVjuh51aG+EVIhXjI5wB4K/7klAEh0UyJ+ECKsZwKAW0IpiOAmQMVWaYi2EeCyUBoiuAAslYpYAH4tFfELJEKpiGCM2dIRM8xHRez73gcpEXOsREM8UfcKRlIi7rIWBbHuI6OQAbHKo5yIffddBDIiHtLJgfhDzTjkQvxJNyNi1cdGITfiaVbEKSAWYicFYrDOpr8cjyIiKi667yAlosmDSIgXfOI3IcMarERAvGXRX0LG3WU+F2LEtB+FXLvObGbEiz61HecGlmRCvO1bXSHKTsNmqbfyn4HFUhF3gEulIt77Hz3yUysNcYPyXwPHAeDrUhBfOdQZe4UjeiY53OeFI2ppj4viI1pOOKKrBSIOTOvTl4Uhbutb1YNCEA1V+pfYGuhXuZeD8NiYY3rDjjDE7ZgyQOd1hkh4x4BNeTwUwrY3pSixGp3QMJblQ9xBRMKiqkxdsRUF0DItR8+a080F6Kk7KXcvq/s7E6Br2RnRStzUSnkJbhgTvYoLFmwMcDJ5x/sqhtppM+bc09DU0dXV0dRwz3UzTqX/wX8AL5Yi61hKoK8AAAAASUVORK5CYII=", // From your original "primary" or "light" (they are same)
    dark: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEIAAABCCAQAAABJXchjAAACt0lEQVR42s3ZvU8TYRzA8V/aAV/AxUEDLSiGKBKJiRFn5cWJEOJQmU38E9wMpAMUBB0NNBEuiAzEgmwmDgX9CocRJCBahLcZESliMXNvwdbihvJSW57nn3vjc/ss3uT69u+cRdYQZIso8y6TZwcJihzQpPhHlKWFxE0H6mGaNWtZI0EtATKOZcXKoyBGjWUyhlTh76Cgwwy1xiovEKOJEkTiXRB9P2MKEHIOigzrimDTDeVFDC8uYllJavrSTwQ2/6JTT4QF53JKnS2qjgzxu2qWz9m8hRyUldjSuEpVkCFdfESkqWxUNrFLZN87JSZgCTyLgzcl/TXgWAQNyHA3kPI3IUi9HMQGeRsArOYybFD2PKHBDDuIDqERwlVZqX+tUNy1lhLEUI75gQoGW8shx8CUCxsRGgKxvETmC9sA+8C0Cuu2B075GTNkDf/gasWKvDHyNgJAw5HtERIgajSjxmceoGRHmjUXsEue6CEHUzAopIxHLPLNf6TUiFoUNhxElFugRm17EupB3EPG7/NHrIOKPYGlGLPLcvgGOI/Z0IxrFZihiWy2iNjrUb8eGuQgC9LDAPmrSwpKZCC7zgp/oSArzziO4S4K/6JoVok4iCNLPV5wZFoZ0I7jCS7ZwLqL7KL/HRyzMaBIR1nx9lH+3ByZ8jZi0B/b6GvHwbLzy24j5FjF6Fj4Dm6WM975EvJODaKPoeUSBVjmM155HxFS3i8xHZKiX4xj0MGKffqmMt55FTFTbTF3yJCJJXfWTvuyptpUL6NskJNVxm23ctE271EYXeRcT7p/+0GUTN2xxR+0UeBHTkoR0DuL2MSdBnehggCwmZOgXfVxgBAsnCsRpEKe4Rpx/6LCYoU1MIcwYGVRkGCUkphGgmylWqWWFSR4REDfRRIQR5kiSJo+FRZ40SeYYJkKj+sT/4VHvuFGgLiwAAAAASUVORK5CYII=", // The dark logo you provided
    light:
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEIAAABCCAQAAABJXchjAAACrklEQVR42s3ZT2scZQDH8Q+bwxptvXhQ2kyikaKxWASxnrV/PIVSeog5C74Eb9KQQ7v1IF3BxgT0EKQ1N6EHD76ETRM8aYhb281GBLOhF7G7CXk8B7NmZ+bZGb+/+/KBgZ2ZZ2QoMWveinVNu7q6djWt+c68DyWG2ojLlmwKx2zToksqojfulraQYm0146I1qe6pkGE9y16Xu+fU7Ak5tqfueTm6ZluIsLarMlVVFyJu2aiUTVgXIm9NIkVTWoL4+905A/aujjCkdZw3QGd1hCHuiXOOaaLPX9K+3QzbF45YS0L/qtb6+DdkaUM4cj95Rt8WhEIQwRf6dE0oDBFccUQntQtFbDnhX90WCkUEn3G41+wVjuh51aG+EVIhXjI5wB4K/7klAEh0UyJ+ECKsZwKAW0IpiOAmQMVWaYi2EeCyUBoiuAAslYpYAH4tFfELJEKpiGCM2dIRM8xHRez73gcpEXOsREM8UfcKRlIi7rIWBbHuI6OQAbHKo5yIffddBDIiHtLJgfhDzTjkQvxJNyNi1cdGITfiaVbEKSAWYicFYrDOpr8cjyIiKi667yAlosmDSIgXfOI3IcMarERAvGXRX0LG3WU+F2LEtB+FXLvObGbEiz61HecGlmRCvO1bXSHKTsNmqbfyn4HFUhF3gEulIt77Hz3yUysNcYPyXwPHAeDrUhBfOdQZe4UjeiY53OeFI2ppj4viI1pOOKKrBSIOTOvTl4UhibuyaHqwAQgGqv1L7A10LFzLwfhsaDO9YccYYnbMGSBzusMkPGPALryeCmFbGtKlsRrNUCG25UPcQUjCoqqMXbEFBdAyLUfPm1POBeiJOyl3L6v7OxOgatmZ0UrctJ5CW4YE72KCxZsDHByecf7KobbabPv3NPQ1NHV1dHScM91M06l/8F/wAvliLrWEqgrwAAAAASUVORK5CYII=", // Your original "light" logo
  },
};

const buttonStyles = computed(() => {
  const themeColors = {
    backgroundColor: colors.bg[props.theme],
    color: colors.fg[props.theme],
    borderColor: colors.border[props.theme],
  };

  return {
    // Core dimensions from BaseButton
    height: "44px",
    minWidth: props.width,
    borderRadius: "8px",
    fontSize: "18px",

    backgroundColor: themeColors.backgroundColor,
    color: themeColors.color,
    border: `1px solid ${themeColors.borderColor}`,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",

    cursor: "pointer",
    fontWeight: "bold",
    transition: "opacity 0.2s ease-in-out, transform 0.1s ease-out",
  };
});

const logoContainerStyles = computed(() => {
  return {
    marginRight: '8px', // Equivalent to mr-2
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };
});

const logoImageStyles = computed(() => {
  return {
    height: '24px', // Equivalent to h-6
    width: 'auto',
  };
});

const logoSrc = computed(() => {
  if (props.theme === "dark") {
    return colors.logo.dark;
  }
  return colors.logo.primary;
});

const handleButtonClick = () => {
  console.log(`Themed button with theme '${props.theme}' was clicked!`);
  emit("click");
};
</script>

<style scoped>
button:hover {
  opacity: 0.8;
}

button:active {
  transform: translateY(1px);
}
</style>
